import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { http, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { HardDrives, ArrowsClockwise, CheckCircle, XCircle, Broadcast, PlugsConnected, ListMagnifyingGlass, MagicWand } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function OltDashboard() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncingAll, setSyncingAll] = useState(false);
  const [onuDrawer, setOnuDrawer] = useState(null);   // {olt, onus, loading, error}
  const [discoverModal, setDiscoverModal] = useState(null); // {olt, result, loading}
  const [subIndex, setSubIndex] = useState([]); // preloaded subscriber list for the "Copy to subscriber" datalist
  const [copyState, setCopyState] = useState({}); // {index: {sub_id, busy}}
  useEffect(() => {
    http.get("/subscribers").then(({ data }) => setSubIndex(data || [])).catch(() => {});
  }, []);
  const bindMac = async (row, subId) => {
    if (!subId) return toast.error("Pick a subscriber first");
    const idx = row.index;
    setCopyState((s) => ({ ...s, [idx]: { sub_id: subId, busy: true } }));
    try {
      const mac = (row.mac || row.serial || "").toUpperCase();
      if (!mac) throw new Error("no MAC on this row");
      await http.patch(`/subscribers/${subId}`, { mac_address: mac, onu_serial: mac });
      toast.success(`Bound ${mac} → ${subIndex.find((x) => x.id === subId)?.username || subId}. Next Auto-link will link this ONU.`);
      setCopyState((s) => ({ ...s, [idx]: { sub_id: subId, busy: false, done: true } }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "bind failed");
      setCopyState((s) => ({ ...s, [idx]: { sub_id: subId, busy: false } }));
    }
  };
  const discover = async (o) => {
    setDiscoverModal({ olt: o, loading: true, result: null });
    const t = toast.loading(`Discovering ONUs on ${o.name}…`);
    try {
      const { data } = await http.post(`/olts/${o.id}/discover-onus`);
      toast.dismiss(t);
      toast.success(`Walked ${data.walked} ONUs · ${data.matched.length} linked · ${data.unmatched.length} unmatched`);
      setDiscoverModal({ olt: o, loading: false, result: data });
    } catch (err) {
      toast.dismiss(t);
      toast.error(err?.response?.data?.detail || "Discovery failed");
      setDiscoverModal(null);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/olts/dashboard/summary");
      setSummary(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const syncAll = async () => {
    setSyncingAll(true);
    const t = toast.loading("Polling every OLT via SNMP…");
    try {
      const { data } = await http.post("/cron/olt-sync/run-now");
      toast.dismiss(t);
      toast.success(`Synced ${data.ok}/${data.total} OLTs (${data.fail} failed)`);
      load();
    } catch { toast.dismiss(t); toast.error("Sync failed"); }
    finally { setSyncingAll(false); }
  };

  const openOnus = async (o) => {
    setOnuDrawer({ olt: o, onus: [], loading: true, error: null });
    try {
      const { data } = await http.get(`/olts/${o.id}/onus`);
      setOnuDrawer({ olt: o, onus: data.onus || [], loading: false, error: data.error || null, vendor: data.vendor });
    } catch (err) {
      setOnuDrawer({ olt: o, onus: [], loading: false, error: err?.response?.data?.detail || "walk failed" });
    }
  };

  const k = summary?.kpis || {};
  const olts = summary?.olts || [];

  return (
    <div className="space-y-6 fade-up" data-testid="olt-dashboard-view">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="uppercase-label">Optical access network</div>
          <h1 className="font-display font-bold text-4xl mt-1">OLT Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Fleet view of every OLT — sync status, port utilisation, active ONUs, sysUpTime per vendor. Cron polls every 5 minutes; hit <b>Sync all now</b> to force a refresh.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate("/olts")} className="rounded-sm h-9 border-border">Manage OLTs</Button>
          <Button onClick={async () => {
            const olts = summary?.olts || [];
            if (!olts.length) return toast.error("No OLTs registered");
            const t = toast.loading(`Auto-linking ONUs across ${olts.length} OLTs…`);
            let ok = 0, matched = 0;
            for (const o of olts) {
              try {
                const { data } = await http.post(`/olts/${o.id}/discover-onus`);
                ok += 1; matched += (data.matched?.length || 0);
              } catch { /* skip */ }
            }
            toast.dismiss(t);
            toast.success(`Auto-link done — ${matched} subscribers linked across ${ok}/${olts.length} OLTs`);
            load();
          }} data-testid="autolink-fleet-btn" variant="outline" className="rounded-sm h-9 border-[hsl(var(--success)/0.4)] text-[hsl(var(--success))] hover:bg-[hsl(var(--success)/0.05)]">
            <MagicWand size={14} className="mr-2" /> Auto-link fleet
          </Button>
          <Button onClick={syncAll} disabled={syncingAll} data-testid="sync-all-btn" className="rounded-sm bg-primary hover:bg-primary/90 h-9">
            <ArrowsClockwise size={14} className={`mr-2 ${syncingAll ? "animate-spin" : ""}`} /> Sync all now
          </Button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Kpi label="OLTs" value={k.total ?? 0} testid="olt-k-total" />
        <Kpi label="Online" value={k.online ?? 0} tone="success" testid="olt-k-online" />
        <Kpi label="Offline" value={k.offline ?? 0} tone={k.offline > 0 ? "danger" : "muted"} testid="olt-k-offline" />
        <Kpi label="Synced" value={k.synced ?? 0} testid="olt-k-synced" />
        <Kpi label="Ports" value={`${k.used_ports ?? 0}/${k.total_ports ?? 0}`} testid="olt-k-ports" />
        <Kpi label="Active ONUs" value={k.active_onus ?? 0} tone="primary" testid="olt-k-onus" />
      </div>

      {/* Vendor breakdown */}
      {summary?.by_vendor && Object.keys(summary.by_vendor).length > 0 && (
        <div className="border border-border bg-card p-4">
          <div className="uppercase-label mb-2">By vendor</div>
          <div className="flex gap-3 flex-wrap">
            {Object.entries(summary.by_vendor).map(([v, c]) => (
              <div key={v} className="flex items-center gap-2 border border-border px-3 py-1.5 rounded-sm text-xs font-mono">
                <span className="uppercase text-muted-foreground">{v}</span>
                <span className="text-primary text-sm">{c}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Fleet table */}
      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead><tr className="border-b border-border">
            <th className="uppercase-label text-left py-2 px-3">Name</th>
            <th className="uppercase-label text-left py-2 px-3">Vendor · Model</th>
            <th className="uppercase-label text-left py-2 px-3">Parent NAS</th>
            <th className="uppercase-label text-left py-2 px-3">Subscribers</th>
            <th className="uppercase-label text-left py-2 px-3">Ports</th>
            <th className="uppercase-label text-left py-2 px-3">Uptime</th>
            <th className="uppercase-label text-left py-2 px-3">Last sync</th>
            <th className="uppercase-label text-left py-2 px-3">Status</th>
            <th className="uppercase-label text-right py-2 px-3">Actions</th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="text-center py-10 text-muted-foreground">loading…</td></tr>}
            {!loading && olts.length === 0 && <tr><td colSpan={9} className="text-center py-10 text-muted-foreground">No OLTs registered.</td></tr>}
            {olts.map((o) => (
              <tr key={o.id} className="border-b border-border/50 hover:bg-accent/20" data-testid={`olt-row-${o.id}`}>
                <td className="py-2 px-3"><div className="font-medium">{o.name}</div><div className="text-[10px] font-mono text-muted-foreground">{o.local_ip || o.ip_address}</div></td>
                <td className="py-2 px-3 text-xs">{o.vendor} · <span className="text-muted-foreground">{o.model || "—"}</span></td>
                <td className="py-2 px-3 text-xs font-mono">{o.parent_nas}</td>
                <td className="py-2 px-3 font-mono text-sm">{o.subscribers}</td>
                <td className="py-2 px-3 font-mono text-xs">{o.used_ports}/{o.total_ports}</td>
                <td className="py-2 px-3 font-mono text-xs">{o.sys_uptime || "—"}</td>
                <td className="py-2 px-3 text-xs">
                  {o.last_sync_at ? (
                    <div className="flex items-center gap-1">
                      {o.last_sync_ok ? <CheckCircle size={12} className="text-[hsl(var(--success))]" /> : <XCircle size={12} className="text-[hsl(var(--destructive))]" />}
                      <span className="font-mono text-muted-foreground">{fmtDate(o.last_sync_at)}</span>
                    </div>
                  ) : <span className="italic text-muted-foreground text-[11px]">never</span>}
                </td>
                <td className="py-2 px-3"><span className="inline-flex items-center gap-2 font-mono text-xs"><span className={`pulse-dot ${o.status}`} />{o.status}</span></td>
                <td className="py-2 px-3 text-right space-x-3">
                  <button onClick={() => openOnus(o)} className="text-xs text-primary hover:text-primary/80 inline-flex items-center gap-1" data-testid={`olt-walk-${o.id}`}>
                    <ListMagnifyingGlass size={12} /> ONUs
                  </button>
                  <button onClick={() => discover(o)} className="text-xs text-[hsl(var(--success))] hover:text-[hsl(var(--success))]/80 inline-flex items-center gap-1" data-testid={`olt-autolink-${o.id}`} title="Walk the OLT's ONU table and auto-link each MAC/serial to a subscriber">
                    <MagicWand size={12} /> Auto-link
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ONU drawer */}
      {onuDrawer && (
        <div className="fixed inset-0 bg-background/80 z-50 flex justify-end" onClick={() => setOnuDrawer(null)}>
          <div className="w-full max-w-2xl bg-card border-l border-border h-full overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="onu-drawer">
            <div className="border-b border-border px-6 py-4 flex items-center justify-between">
              <div>
                <div className="uppercase-label">ONU walk</div>
                <div className="font-display font-bold text-xl">{onuDrawer.olt.name} <span className="text-muted-foreground text-sm font-mono">· {onuDrawer.olt.vendor}</span></div>
              </div>
              <button onClick={() => setOnuDrawer(null)} className="text-muted-foreground hover:text-foreground text-2xl leading-none">×</button>
            </div>
            <div className="p-6">
              {onuDrawer.loading && <div className="text-sm text-muted-foreground">SNMP walking… this may take up to 10s</div>}
              {onuDrawer.error && !onuDrawer.loading && (
                <div className="border border-[hsl(var(--destructive)/0.4)] bg-[hsl(var(--destructive)/0.1)] p-3 rounded-sm text-xs text-[hsl(var(--destructive))] font-mono">
                  {onuDrawer.error}
                </div>
              )}
              {!onuDrawer.loading && !onuDrawer.error && onuDrawer.onus.length === 0 && (
                <div className="text-sm text-muted-foreground italic">No ONUs returned. Vendor MIB may need mapping or the OLT has no registered ONUs.</div>
              )}
              {onuDrawer.onus.length > 0 && (
                <div>
                  <div className="uppercase-label mb-2">Discovered {onuDrawer.onus.length} ONUs</div>
                  <div className="overflow-x-auto border border-border">
                    <table className="w-full text-xs font-mono min-w-[700px]">
                      <thead className="bg-secondary/40">
                        <tr className="border-b border-border">
                          <th className="text-left py-1.5 px-2 uppercase-label">Idx</th>
                          <th className="text-left py-1.5 px-2 uppercase-label">Port</th>
                          <th className="text-left py-1.5 px-2 uppercase-label">Serial</th>
                          <th className="text-left py-1.5 px-2 uppercase-label">Status</th>
                          <th className="text-right py-1.5 px-2 uppercase-label">Rx dBm</th>
                          <th className="text-right py-1.5 px-2 uppercase-label">Dist m</th>
                          <th className="text-left py-1.5 px-2 uppercase-label">Description</th>
                          <th className="text-left py-1.5 px-2 uppercase-label">Subscriber</th>
                        </tr>
                      </thead>
                      <tbody>
                        {onuDrawer.onus.map((o, i) => (
                          <tr key={i} className="border-b border-border/50 hover:bg-accent/20" data-testid={`onu-row-${o.index}`}>
                            <td className="py-1.5 px-2 text-muted-foreground">{o.index}</td>
                            <td className="py-1.5 px-2">{o.pon_port || "—"}</td>
                            <td className="py-1.5 px-2 break-all">{o.serial || "—"}</td>
                            <td className="py-1.5 px-2">
                              <span className="inline-flex items-center gap-1.5">
                                <span className={`pulse-dot ${o.status === "online" ? "online" : "offline"}`} />
                                {o.status || "—"}
                              </span>
                            </td>
                            <td className="py-1.5 px-2 text-right">{o.rx_power || (onuDrawer.vendor === "Other" || onuDrawer.olt?.vendor === "Other" ? <span title="V-SOL V1600D-16D EPON firmware does not expose per-ONU RX power on the public community. Try the Diagnose modal's custom-walk with community=private and root=1.3.6.1.4.1.3320.101.10.1.5.1.14, or upgrade OLT firmware." className="text-muted-foreground italic">n/a</span> : "—")}</td>
                            <td className="py-1.5 px-2 text-right">{o.distance || "—"}</td>
                            <td className="py-1.5 px-2 truncate max-w-[160px]">{o.description || "—"}</td>
                            <td className="py-1.5 px-2 text-[10px]">
                              {o.subscriber ? (
                                <span>{o.subscriber.full_name} <span className="text-muted-foreground">· {o.subscriber.username}</span></span>
                              ) : <span className="text-muted-foreground italic">unmapped</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* Raw dump — collapsible fallback for vendors without parsed columns */}
                  {!onuDrawer.onus[0]?.serial && (
                    <details className="mt-3 text-xs font-mono">
                      <summary className="cursor-pointer text-muted-foreground uppercase-label">Raw OID columns (no vendor parser matched)</summary>
                      <div className="space-y-1.5 mt-2">
                        {onuDrawer.onus.map((o, i) => (
                          <div key={i} className="border border-border p-2">
                            <div className="text-primary text-[11px]">idx {o.index}</div>
                            <div className="grid grid-cols-3 gap-x-3 gap-y-0.5 text-[10px] text-muted-foreground mt-1">
                              {Object.entries(o.raw).slice(0, 15).map(([k, v]) => (
                                <div key={k} className="truncate" title={`col ${k} = ${v}`}><span className="text-[9px]">col{k}</span> <span className="text-foreground">{v}</span></div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Auto-Discover ONUs modal */}
      {discoverModal && (
        <div className="fixed inset-0 bg-background/80 z-50 flex justify-end" onClick={() => setDiscoverModal(null)}>
          <div className="w-full max-w-3xl bg-card border-l border-border h-full overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="discover-modal">
            <div className="border-b border-border px-6 py-4 flex items-center justify-between">
              <div>
                <div className="uppercase-label">Auto-discover ONUs</div>
                <div className="font-display font-bold text-xl">{discoverModal.olt.name}</div>
              </div>
              <button onClick={() => setDiscoverModal(null)} className="text-muted-foreground hover:text-foreground text-2xl leading-none">×</button>
            </div>
            <div className="p-6 space-y-4">
              {discoverModal.loading && <div className="text-sm text-muted-foreground font-mono">Walking SNMP…</div>}
              {!discoverModal.loading && discoverModal.result && (
                <>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="border border-border p-3"><div className="uppercase-label">Walked</div><div className="text-2xl font-display font-bold mt-1">{discoverModal.result.walked}</div></div>
                    <div className="border border-[hsl(var(--success)/0.4)] p-3"><div className="uppercase-label text-[hsl(var(--success))]">Linked to subscribers</div><div className="text-2xl font-display font-bold mt-1 text-[hsl(var(--success))]">{discoverModal.result.matched?.length ?? 0}</div></div>
                    <div className="border border-[hsl(var(--warning)/0.4)] p-3"><div className="uppercase-label text-[hsl(var(--warning))]">Unmatched</div><div className="text-2xl font-display font-bold mt-1 text-[hsl(var(--warning))]">{discoverModal.result.unmatched?.length ?? 0}</div></div>
                  </div>

                  {discoverModal.result.matched?.length > 0 && (
                    <div className="border border-border" data-testid="matched-list">
                      <div className="uppercase-label p-3 border-b border-border">Linked</div>
                      <table className="w-full text-xs font-mono">
                        <thead className="bg-secondary/40"><tr>
                          <th className="text-left p-2 uppercase-label">Idx</th>
                          <th className="text-left p-2 uppercase-label">Serial / MAC</th>
                          <th className="text-left p-2 uppercase-label">Subscriber</th>
                          <th className="text-right p-2 uppercase-label">Rx dBm</th>
                          <th className="text-left p-2 uppercase-label">Status</th>
                        </tr></thead>
                        <tbody>
                          {discoverModal.result.matched.map((m, i) => (
                            <tr key={i} className="border-t border-border">
                              <td className="p-2">{m.index}</td>
                              <td className="p-2 break-all">{m.serial || m.mac || "—"}</td>
                              <td className="p-2 text-primary">{m.full_name} <span className="text-muted-foreground">· {m.username}</span></td>
                              <td className="p-2 text-right">{m.olt_rx_power || m.rx_power || "—"}</td>
                              <td className="p-2">{m.status || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {discoverModal.result.unmatched?.length > 0 && (
                    <div className="border border-[hsl(var(--warning)/0.4)]" data-testid="unmatched-list">
                      <div className="uppercase-label p-3 border-b border-[hsl(var(--warning)/0.4)] text-[hsl(var(--warning))]">Unmatched — no subscriber with matching serial or MAC</div>
                      <table className="w-full text-xs font-mono">
                        <thead className="bg-[hsl(var(--warning)/0.05)]"><tr>
                          <th className="text-left p-2 uppercase-label">Idx</th>
                          <th className="text-left p-2 uppercase-label">Port</th>
                          <th className="text-left p-2 uppercase-label">Serial / MAC</th>
                          <th className="text-left p-2 uppercase-label">Description</th>
                          <th className="text-right p-2 uppercase-label">Rx dBm</th>
                          <th className="text-left p-2 uppercase-label">Status</th>
                          <th className="text-left p-2 uppercase-label">Bind to subscriber</th>
                        </tr></thead>
                        <tbody>
                          {discoverModal.result.unmatched.map((u, i) => {
                            const st = copyState[u.index] || {};
                            return (
                            <tr key={i} className="border-t border-border">
                              <td className="p-2">{u.index}</td>
                              <td className="p-2">{u.pon_port || "—"}</td>
                              <td className="p-2 break-all text-foreground">{u.serial || u.mac || "—"}</td>
                              <td className="p-2 truncate max-w-[160px]">{u.description || "—"}</td>
                              <td className="p-2 text-right">{u.olt_rx_power || u.rx_power || "—"}</td>
                              <td className="p-2">{u.status || "—"}</td>
                              <td className="p-2">
                                <div className="flex gap-1 items-center">
                                  <input
                                    list={`subs-list-${discoverModal.olt.id}`}
                                    placeholder="type user…"
                                    className="h-6 px-1.5 text-[10px] font-mono bg-secondary border border-border rounded-sm w-32"
                                    data-testid={`bind-input-${u.index}`}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      const match = subIndex.find((s) => `${s.username} · ${s.full_name}` === val || s.username === val);
                                      setCopyState((s) => ({ ...s, [u.index]: { ...(s[u.index] || {}), sub_id: match?.id || null } }));
                                    }}
                                    defaultValue=""
                                    disabled={st.done}
                                  />
                                  <button
                                    data-testid={`bind-btn-${u.index}`}
                                    className="text-[10px] px-1.5 h-6 rounded-sm border border-primary text-primary hover:bg-primary/10 disabled:opacity-40"
                                    disabled={!st.sub_id || st.busy || st.done}
                                    onClick={() => bindMac(u, st.sub_id)}
                                  >
                                    {st.done ? "✓ bound" : st.busy ? "…" : "Bind"}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );})}
                        </tbody>
                      </table>
                      <datalist id={`subs-list-${discoverModal.olt.id}`}>
                        {subIndex.map((s) => (
                          <option key={s.id} value={`${s.username} · ${s.full_name}`}>{s.full_name}</option>
                        ))}
                      </datalist>
                      <div className="p-3 text-[10px] text-muted-foreground border-t border-[hsl(var(--warning)/0.4)]">
                        Hint: Copy any serial above, open the matching subscriber's <b>Inventories</b> tab, paste it into <b>ONU Serial</b> and save. Next auto-sync will link them.
                      </div>
                    </div>
                  )}

                  {discoverModal.result.tried_roots && (
                    <details className="text-[10px] text-muted-foreground">
                      <summary className="cursor-pointer uppercase-label">MIB roots tried ({discoverModal.result.tried_roots.length})</summary>
                      <div className="mt-1 space-y-0.5 font-mono">
                        {discoverModal.result.tried_roots.map((t, i) => <div key={i}>→ {t.root} · rows={t.rows} · vendor={t.vendor}{t.error ? ` · err=${t.error}` : ""}</div>)}
                      </div>
                    </details>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, tone = "muted", testid }) {
  const cls = tone === "danger" ? "text-[hsl(var(--destructive))]" : tone === "success" ? "text-[hsl(var(--success))]" : tone === "primary" ? "text-primary" : "text-foreground";
  return (
    <div className="border border-border bg-card p-3" data-testid={testid}>
      <div className="uppercase-label">{label}</div>
      <div className={`font-display font-bold text-2xl mt-1 ${cls}`}>{value}</div>
    </div>
  );
}
